import { useState, useEffect } from 'react';
import { applyTheme } from '../utils/themes';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';

export default function ServerSelectPage() {
  const { user, logout } = useAuth();

  // Always show Brazyy Pink on server select page
  useEffect(() => { applyTheme('brazyy_pink'); }, []);
  const navigate = useNavigate();
  const [guilds, setGuilds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('brazyy_token');
    if (!token) {
      setError('Session expired. Please log in again.');
      setLoading(false);
      return;
    }
    api.get('/api/guilds/mine')
      .then(res => setGuilds(res.data))
      .catch((err) => {
        console.error('Guilds error:', err.response?.status, err.response?.data);
        setError('Failed to load servers. Please try again.');
      })
      .finally(() => setLoading(false));
  }, [user]);

  const filtered = guilds.filter(g =>
    g.name.toLowerCase().includes(search.toLowerCase())
  );

  const activated = filtered.filter(g => g.activated);
  const notActivated = filtered.filter(g => !g.activated);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--gradient-hero)',
      fontFamily: 'var(--font-body)',
    }}>
      {/* Header */}
      <header style={{
        background: 'rgba(255,255,255,0.7)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--border)',
        padding: '16px 40px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '50%',
            background: 'var(--gradient-accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '18px'
          }}>🕐</div>
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: '20px',
            color: 'var(--accent-primary)', fontWeight: 700,
          }}>Brazyy's Clock-In</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img
                src={user.avatar
                  ? `https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png`
                  : `https://cdn.discordapp.com/embed/avatars/0.png`}
                alt={user.username}
                style={{ width: '32px', height: '32px', borderRadius: '50%', border: '2px solid var(--border)' }}
              />
              <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
                {user.username}
              </span>
            </div>
          )}
          <button onClick={logout} style={{
            background: 'transparent', border: '1px solid var(--border)',
            borderRadius: '8px', padding: '8px 16px',
            fontSize: '13px', color: 'var(--text-secondary)',
            cursor: 'pointer', fontFamily: 'var(--font-body)',
            transition: 'all 0.15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.color = 'var(--accent-primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
          >Logout</button>
        </div>
      </header>

      {/* Content */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '48px 24px' }}>
        <div style={{ marginBottom: '40px', textAlign: 'center' }}>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '36px', fontWeight: 900,
            color: 'var(--text-primary)', marginBottom: '12px',
          }}>Select a Server</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>
            Only servers where you have admin permissions are shown.
          </p>
        </div>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: '32px' }}>
          <span style={{
            position: 'absolute', left: '16px', top: '50%',
            transform: 'translateY(-50%)', fontSize: '18px'
          }}>🔍</span>
          <input
            type="text"
            placeholder="Search your servers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '14px 16px 14px 46px',
              border: '2px solid var(--border)', borderRadius: '12px',
              fontSize: '15px', fontFamily: 'var(--font-body)',
              background: 'var(--bg-card)', color: 'var(--text-primary)',
              outline: 'none', boxSizing: 'border-box',
              transition: 'border-color 0.2s',
            }}
            onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
            onBlur={e => e.target.style.borderColor = 'var(--border)'}
          />
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>⏳</div>
            Loading your servers...
          </div>
        )}

        {error && (
          <div style={{
            background: '#FFF0F0', border: '1px solid #FFB0B0',
            borderRadius: '12px', padding: '16px 20px',
            color: '#CC0000', marginBottom: '24px',
          }}>{error}</div>
        )}

        {/* Activated servers */}
        {activated.length > 0 && (
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '14px', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '1px', color: 'var(--text-muted)',
              marginBottom: '16px',
            }}>✅ Active Servers</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: '16px',
            }}>
              {activated.map(guild => (
                <GuildCard key={guild.id} guild={guild} activated onClick={() => navigate(`/dashboard/${guild.id}`)} />
              ))}
            </div>
          </div>
        )}

        {/* Not activated servers */}
        {notActivated.length > 0 && (
          <div>
            <h2 style={{
              fontSize: '14px', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '1px', color: 'var(--text-muted)',
              marginBottom: '16px',
            }}>🔒 Not Activated</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: '16px',
            }}>
              {notActivated.map(guild => (
                <GuildCard key={guild.id} guild={guild} activated={false} onClick={null} />
              ))}
            </div>
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔍</div>
            No servers found matching "{search}"
          </div>
        )}
      </div>
    </div>
  );
}

function GuildCard({ guild, activated, onClick }) {
  const iconUrl = guild.icon
    ? `https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png`
    : null;

  return (
    <div
      onClick={activated ? onClick : null}
      style={{
        background: 'var(--bg-card)',
        border: `2px solid ${activated ? 'var(--border)' : 'var(--border)'}`,
        borderRadius: '16px',
        padding: '20px',
        cursor: activated ? 'pointer' : 'default',
        opacity: activated ? 1 : 0.6,
        transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
        boxShadow: 'var(--shadow-card)',
        display: 'flex', alignItems: 'center', gap: '16px',
      }}
      onMouseEnter={e => {
        if (activated) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = 'var(--shadow)';
          e.currentTarget.style.borderColor = 'var(--accent-primary)';
        }
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'var(--shadow-card)';
        e.currentTarget.style.borderColor = 'var(--border)';
      }}
    >
      {iconUrl ? (
        <img src={iconUrl} alt={guild.name} style={{
          width: '52px', height: '52px', borderRadius: '50%',
          border: '2px solid var(--border)',
        }} />
      ) : (
        <div style={{
          width: '52px', height: '52px', borderRadius: '50%',
          background: 'var(--gradient-accent)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '22px', fontWeight: 700, color: '#fff',
          flexShrink: 0,
        }}>
          {guild.name.charAt(0).toUpperCase()}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '15px', fontWeight: 700,
          color: 'var(--text-primary)', marginBottom: '4px',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{guild.name}</div>
        <div style={{ fontSize: '12px', color: activated ? 'var(--success)' : 'var(--text-muted)' }}>
          {activated ? '✅ Active — Click to manage' : '🔒 Not activated'}
        </div>
      </div>
    </div>
  );
}
